/**
 * Price typography for the campaign variant of Compact Pricing and the Pricing
 * Teaser (SH-415, Figma node 513:307).
 *
 * **Campaign variant B only.** Variants A and C keep the price styling they
 * had before, so these constants are applied by the B wrapper components rather
 * than by the shared components they wrap. The `fixedPrice` *fallback* itself is
 * a functional fix and works on every variant — only its styling is scoped.
 *
 * Colours map to project tokens: `#002b60` is `--color-price` (added for this
 * design); `#28190a` is exactly `--color-secondary`; `#82786f` uses
 * `--color-muted` (`#79726b`).
 */

/**
 * A price resolved from a `pricePlaceholder`, e.g. "ab 9,35 €/Std."
 *
 * Design: Montserrat Medium 24px `#002b60`, line-height normal — taken from
 * `pricing-value` (Figma 513:312).
 */
export const CAMPAIGN_PRICE =
  'text-price text-2xl leading-[normal] font-medium';

/**
 * A literal `fixedPrice`, where the editor's text styles carry meaning:
 *
 * | Editor style      | Element    | Meaning                           | Design (Figma 513:322) |
 * | ----------------- | ---------- | --------------------------------- | ---------------------- |
 * | normal            | —          | the lead-in, e.g. "8 Std für nur" | Regular 16px `#28190a` |
 * | **bold**          | `<strong>` | the current price, e.g. "29 €"    | ExtraBold 32px `#002b60` |
 * | ~~strikethrough~~ | `<del>`    | the crossed-out original price    | Medium 24px `#82786f`, struck |
 *
 * `<strong>` and `<del>` are what `@strapi/blocks-react-renderer` emits for
 * those two modifiers.
 *
 * **The base style owns the whole richtext and must replace the slot's own
 * price classes.** Plain text in a price slot is always the lead-in, never the
 * price — letting a slot's 24px/Medium price style through renders
 * "8 Std für nur" as though it were the price.
 *
 * NB `globals.css` renders `b`/`strong` at weight 500 site-wide (a deliberate
 * 2026-09-02 decision for CMS-authored bold). That is below the price weight
 * here, so the bold rule restates it rather than relying on the global one.
 *
 * NB the design sets the currency symbol two steps smaller than the number
 * ("29" at 32px, "€" at 28px) by splitting them into separate nodes. A single
 * bold run cannot express that, so the whole bold text renders at 32px. That
 * simplification was reviewed and accepted — it reads correctly.
 */
export const CAMPAIGN_PRICE_RICHTEXT = [
  // Lead-in: the design's plain text style, not the slot's price style.
  'text-secondary text-base font-normal',
  // The row is a centred flex line; each text run, <strong> and <del> becomes
  // a flex item, which is what produces the 15px gaps without the editor
  // typing them. Wraps rather than overflowing in the narrow card slots.
  '[&_p]:m-0 [&_p]:flex [&_p]:flex-wrap [&_p]:items-center [&_p]:justify-center [&_p]:gap-[15px]',
  '[&_strong]:text-price [&_strong]:text-[32px] [&_strong]:font-extrabold',
  '[&_del]:text-muted [&_del]:text-2xl [&_del]:font-medium [&_del]:line-through',
].join(' ');
